// StudyDost — Chat Page (Professional Redesign)
// frontend/src/pages/ChatPage.js

import React, { useState, useRef, useEffect } from 'react';
import { Send, ImagePlus, Mic, Zap, Crown, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { questionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import FreeLimitModal from '../components/FreeLimitModal';

const PLAN_CONFIG = {
  MONTHLY: { label: 'Monthly Plan', color: '#4F46E5', gradient: 'linear-gradient(135deg, #4F46E5, #7C3AED)' },
  QUARTERLY: { label: 'Quarterly Plan', color: '#7C3AED', gradient: 'linear-gradient(135deg, #7C3AED, #4F46E5)' },
  ANNUAL: { label: 'Annual Plan ⭐', color: '#D97706', gradient: 'linear-gradient(135deg, #D97706, #F59E0B)' },
  FREE: { label: null, color: '#4F46E5', gradient: 'linear-gradient(135deg, #4F46E5, #6366F1)' },
};

const QUICK_QUESTIONS = [
  'How does photosynthesis work?',
  "What are Newton's three laws?",
  'Explain the Pythagoras theorem',
  'Difference between mitosis and meiosis?',
  'What caused the French Revolution?',
  'Explain the water cycle',
];

function isActivePaidPlan(user) {
  if (!user) return false;
  const paid = ['MONTHLY', 'QUARTERLY', 'ANNUAL'];
  if (!paid.includes(user.plan) && !paid.includes(user.activePlan)) return false;
  const expiry = user.planExpiry || user.planExpiresAt;
  if (expiry && new Date(expiry) < new Date()) return false;
  return true;
}

function cleanText(text) {
  if (!text) return '';
  return text
    .replace(/(?<!\*)\*(?!\*)([^*\n]+)(?<!\*)\*(?!\*)/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function renderMessage(text) {
  if (!text) return text;
  const lines = cleanText(text).split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const rendered = parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**'))
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      return part;
    });
    return <React.Fragment key={i}>{rendered}{i < lines.length - 1 && <br />}</React.Fragment>;
  });
}

export default function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { base64, mimeType, previewUrl, file }
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isPaid = isActivePaidPlan(user);
  const activePlan = user?.activePlan || user?.plan;
  const planConfig = PLAN_CONFIG[activePlan] || PLAN_CONFIG.FREE;

  const { data: usage } = useQuery({
    queryKey: ['usage'],
    queryFn: () => questionAPI.getUsage().then(r => r.data),
    refetchInterval: 30000,
    enabled: !isPaid,
  });
  const freeLeft = isPaid ? null : (usage?.freeLeft ?? 5);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const name = user?.name ? user.name.split(' ')[0] : null;
    const classInfo = user?.studentClass ? `Class ${user.studentClass}` : null;

    setMessages([{
      id: 'welcome',
      role: 'bot',
      text: `Namaste${name ? `, ${name}` : ''}! 👋\n\nMain StudyDost hoon${classInfo ? ` — aapka ${classInfo} AI tutor` : ''}. Text likhein, photo bhejein, ya voice note se questions poochein!\n\n${isPaid ? `✨ *${planConfig.label}* — Unlimited questions active!` : `📊 Aaj ke free questions: ${freeLeft ?? 5}/5`}`,
      timestamp: new Date(),
    }]);
  }, [user]); // eslint-disable-line

  const askMutation = useMutation({
    mutationFn: ({ text, history }) => questionAPI.ask(text, user?.studentClass, history),
    onSuccess: (res) => {
      const data = res.data;
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'thinking'),
        {
          id: Date.now().toString(),
          role: 'bot',
          text: data.answer,
          subject: data.subject,
          freeLeft: isPaid ? null : data.freeQuestionsRemaining,
          xp: data.xp || null,
          diagram: data.diagram || null,
          timestamp: new Date(),
        }
      ]);
      queryClient.invalidateQueries(['usage']);
    },
    onError: (err) => {
      setMessages(prev => prev.filter(m => m.id !== 'thinking'));
      if (err.response?.status === 402) setShowLimitModal(true);
      else toast.error('Something went wrong. Please try again.');
    },
  });

  const handleSend = () => {
    if (pendingImage) { handleSendWithImage(); return; }
    const text = input.trim();
    if (!text || askMutation.isPending) return;
    setMessages(prev => [
      ...prev,
      { id: Date.now().toString(), role: 'user', text, timestamp: new Date() },
      { id: 'thinking', role: 'thinking', timestamp: new Date() },
    ]);
    setInput('');
    // Build last 4 turns (excluding welcome + thinking messages)
    const history = messages
      .filter(m => m.role === 'user' || m.role === 'bot')
      .filter(m => m.id !== 'welcome')
      .slice(-4)
      .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));
    askMutation.mutate({ text, history });
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };


  // Step 1: User selects image → show preview in input box
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images supported'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const previewUrl = URL.createObjectURL(file);
    setPendingImage({ base64, mimeType: file.type, previewUrl, fileName: file.name });
    e.target.value = '';
    textareaRef.current?.focus();
  };

  // Step 2: User presses Send with image attached
  const handleSendWithImage = async () => {
    if (askMutation.isPending) return;
    const caption = input.trim();
    const img = pendingImage;
    setPendingImage(null);
    setInput('');

    // Show image thumbnail in chat
    const imgMsgId = 'img-' + Date.now();
    setMessages(prev => [
      ...prev,
      {
        id: imgMsgId,
        role: 'user',
        text: caption || '',
        imagePreview: img.previewUrl,
        timestamp: new Date(),
      },
      { id: 'thinking', role: 'thinking', timestamp: new Date() },
    ]);

    try {
      const res = await questionAPI.askWithImage(img.base64, img.mimeType, caption, user?.studentClass);
      const data = res.data;
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'thinking'),
        { id: Date.now().toString(), role: 'bot', text: data.answer, subject: data.subject, freeLeft: data.freeQuestionsRemaining, xp: data.xp || null, timestamp: new Date() },
      ]);
      queryClient.invalidateQueries(['usage']);
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== 'thinking'));
      if (err.response?.status === 402) setShowLimitModal(true);
      else toast.error('Image process nahi hui. Try again.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleQuickQuestion = (q) => {
    setInput(q.replace(/[📐🔬🗺️📖⚡🌿]\s/, ''));
    textareaRef.current?.focus();
  };

  return (
    <div style={styles.container}>
      {/* Top bar */}
      <div style={{ ...styles.topBar, background: planConfig.gradient }}>
        <div style={styles.topLeft}>
          <img src="/studydost_logo.png" alt="StudyDost" style={{ height: 40, width: 40, borderRadius: 12, objectFit: "contain", background: "#EEF2FF" }} />
          <div>
            <div style={styles.topTitle}>StudyDost</div>
            <div style={styles.topSub}>
              {isPaid ? `${planConfig.label} Active` : `${freeLeft ?? 5}/5 free questions aaj`}
            </div>
          </div>
        </div>
        <div style={styles.topRight}>
          {!isPaid && (
            <button style={styles.upgradeChip} onClick={() => navigate('/plans')}>
              <Crown size={13} />
              Upgrade
            </button>
          )}
          {isPaid && (
            <div style={styles.paidChip}>
              <Sparkles size={13} />
              Unlimited
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {messages.map(msg => (
          <div key={msg.id} style={msg.role === 'user' ? styles.userRow : styles.botRow}>
            {msg.role !== 'user' && (
              <img src="/studydost_logo.png" alt="SD" style={{ width: 28, height: 28, borderRadius: 8, objectFit: "contain", background: "#EEF2FF", flexShrink: 0 }} />
            )}
            <div style={msg.role === 'user' ? styles.userBubble : msg.role === 'thinking' ? styles.thinkingBubble : styles.botBubble}>
              {msg.imagePreview && (
                <img src={msg.imagePreview} alt="uploaded" style={styles.msgImageThumb} />
              )}
              {msg.role === 'thinking' ? (
                <div style={styles.typingIndicator}>
                  <span style={{ ...styles.typingDot, animationDelay: '0ms' }} />
                  <span style={{ ...styles.typingDot, animationDelay: '160ms' }} />
                  <span style={{ ...styles.typingDot, animationDelay: '320ms' }} />
                </div>
              ) : (
                <>
                  <div style={msg.role === 'user' ? styles.userText : styles.botText}>
                    {renderMessage(msg.text)}
                  </div>
                  {msg.subject && msg.subject !== 'OTHER' && (
                    <div style={styles.subjectTag}>{msg.subject}</div>
                  )}
                  {!isPaid && msg.freeLeft !== null && msg.freeLeft !== undefined && msg.role === 'bot' && (
                    <div style={styles.freeTag}>
                      {msg.freeLeft === 0
                        ? '⚠️ Daily limit reached — upgrade for unlimited'
                        : `${msg.freeLeft} free questions bacha aaj`}
                    </div>
                  )}
                  {msg.diagram && msg.role === 'bot' && (
                    <div style={styles.diagramBox}>
                      <img
                        src={msg.diagram.imageUrl}
                        alt={msg.diagram.caption}
                        style={styles.diagramImg}
                        onError={e => { e.target.parentNode.style.display = 'none'; }}
                      />
                      {msg.diagram.caption && (
                        <div style={styles.diagramCaption}>{msg.diagram.caption.split('\n')[0]}</div>
                      )}
                    </div>
                  )}
                  {msg.xp && msg.role === 'bot' && (
                    <div style={styles.xpChip}>
                      {msg.xp.levelUp && (
                        <div style={styles.xpLevelUp}>🎉 Level Up → {msg.xp.level}!</div>
                      )}
                      {msg.xp.newBadges?.length > 0 && (
                        <div style={styles.xpBadge}>🏅 New badge earned!</div>
                      )}
                      <div style={styles.xpRow}>
                        <div style={styles.xpLeft}>
                          <span style={styles.xpFlame}>{msg.xp.streak >= 7 ? '🔥' : msg.xp.streak >= 3 ? '⚡' : '✨'}</span>
                          <span style={styles.xpText}>+{msg.xp.earned} XP · {msg.xp.total} total</span>
                        </div>
                        <div style={styles.streakDots}>
                          {['M','T','W','T','F','S','S'].map((day, i) => (
                            <div key={i} style={styles.streakDotWrap}>
                              <div style={{
                                ...styles.streakDot,
                                background: i < msg.xp.streak % 7 || msg.xp.streak >= 7
                                  ? '#F59E0B' : '#E5E7EB',
                              }} />
                              <span style={styles.streakDayLabel}>{day}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {/* Free limit warning */}
        {!isPaid && freeLeft !== null && freeLeft <= 2 && freeLeft > 0 && (
          <div style={styles.limitBanner}>
            <Zap size={15} color="#D97706" />
            <span>Sirf <strong>{freeLeft}</strong> free questions bacha!</span>
            <button style={styles.limitBannerBtn} onClick={() => navigate('/plans')}>
              Upgrade <ChevronRight size={13} />
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick questions (only when empty) */}
      {messages.length <= 1 && (
        <div style={styles.quickSection}>
          <div style={styles.quickTitle}>What would you like to know? 👇</div>
          <div style={styles.quickGrid}>
            {QUICK_QUESTIONS.map((q, i) => (
              <button key={i} style={styles.quickBtn} onClick={() => handleQuickQuestion(q)}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div style={styles.inputSection}>
        {/* Image preview in input box */}
        {pendingImage && (
          <div style={styles.imagePreviewBox}>
            <img src={pendingImage.previewUrl} alt="preview" style={styles.imagePreviewThumb} />
            <div style={styles.imagePreviewName}>{pendingImage.fileName}</div>
            <button style={styles.removeImageBtn} onClick={() => setPendingImage(null)}>✕</button>
          </div>
        )}
        <div style={styles.inputBox}>
          <textarea
            ref={textareaRef}
            style={styles.textarea}
            placeholder={pendingImage ? "Add a question about this image... (optional)" : "Apna questions likhein..."}
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={askMutation.isPending}
          />
          <div style={styles.inputActions}>
            <button
              style={styles.iconBtn}
              onClick={() => fileInputRef.current?.click()}
              title="Photo bhejo"
            >
              <ImagePlus size={20} color="#94A3B8" />
            </button>
            <button style={styles.iconBtn} title="Voice note">
              <Mic size={20} color="#94A3B8" />
            </button>
            <button
              style={{
                ...styles.sendBtn,
                background: input.trim() && !askMutation.isPending
                  ? 'linear-gradient(135deg, #4F46E5, #7C3AED)'
                  : '#E2E8F0',
              }}
              onClick={handleSend}
              disabled={!input.trim() || askMutation.isPending}
            >
              {askMutation.isPending
                ? <div style={styles.btnSpinner} />
                : <Send size={18} color={input.trim() ? '#FFFFFF' : '#94A3B8'} />
              }
            </button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
      </div>

      {showLimitModal && <FreeLimitModal onClose={() => setShowLimitModal(false)} />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes typing { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  container: { height: 'calc(100dvh - 64px)', display: 'flex', flexDirection: 'column', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif" },
  topBar: { padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  topLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  topAvatar: { fontSize: 32, background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  topTitle: { fontSize: 16, fontWeight: 700, color: '#FFFFFF', fontFamily: "'Sora', sans-serif" },
  topSub: { fontSize: 12, color: 'rgba(255,255,255,0.8)' },
  topRight: {},
  upgradeChip: { display: 'flex', alignItems: 'center', gap: 5, background: '#F59E0B', color: '#FFFFFF', border: 'none', borderRadius: 100, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  paidChip: { display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.2)', color: '#FFFFFF', borderRadius: 100, padding: '6px 14px', fontSize: 12, fontWeight: 600 },

  messages: { flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 12 },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  botRow: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  botAvatarSmall: { fontSize: 22, background: '#EEF2FF', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  userBubble: { maxWidth: '78%', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', borderRadius: '18px 18px 4px 18px', padding: '12px 16px' },
  botBubble: { maxWidth: '82%', background: '#FFFFFF', borderRadius: '4px 18px 18px 18px', padding: '14px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F1F5F9' },
  thinkingBubble: { background: '#FFFFFF', borderRadius: '4px 18px 18px 18px', padding: '14px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F1F5F9' },
  userText: { fontSize: 15, color: '#FFFFFF', lineHeight: 1.6 },
  botText: { fontSize: 15, color: '#1E293B', lineHeight: 1.7, whiteSpace: 'pre-wrap' },
  subjectTag: { marginBottom: 6, fontSize: 10, fontWeight: 700, color: '#4F46E5', background: '#EEF2FF', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 100, letterSpacing: '0.04em' },
  diagramBox: {
    marginTop: 10,
    borderRadius: 10,
    overflow: 'hidden',
    border: '1px solid #E2E8F0',
  },
  diagramImg: {
    width: '100%',
    display: 'block',
    maxHeight: 220,
    objectFit: 'contain',
    background: '#F8FAFF',
  },
  diagramCaption: {
    fontSize: 11,
    color: '#64748B',
    padding: '4px 8px',
    background: '#F8FAFF',
    textAlign: 'center',
  },
  xpChip: {
    marginTop: 8,
    background: '#FFFBEB',
    borderRadius: 10,
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    border: '1px solid #FDE68A',
  },
  xpRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  xpLeft: { display: 'flex', alignItems: 'center', gap: 4 },
  xpFlame: { fontSize: 14 },
  xpText: { fontSize: 11, fontWeight: 600, color: '#92400E' },
  streakDots: { display: 'flex', gap: 3, alignItems: 'center' },
  streakDotWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  streakDot: { width: 10, height: 10, borderRadius: '50%' },
  streakDayLabel: { fontSize: 8, color: '#9CA3AF', fontWeight: 600 },
  xpLevelUp: { fontSize: 11, fontWeight: 700, color: '#059669' },
  xpBadge: { fontSize: 11, fontWeight: 600, color: '#D97706' },
  freeTag: { marginTop: 8, fontSize: 11, color: '#94A3B8', borderTop: '1px solid #F1F5F9', paddingTop: 8 },
  typingIndicator: { display: 'flex', gap: 5, alignItems: 'center', height: 20 },
  typingDot: { width: 8, height: 8, borderRadius: '50%', background: '#CBD5E1', display: 'inline-block', animation: 'typing 1.2s infinite ease-in-out' },
  limitBanner: { display: 'flex', alignItems: 'center', gap: 8, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '10px 14px', fontSize: 13, color: '#92400E', margin: '0 4px' },
  limitBannerBtn: { display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto', background: '#D97706', color: '#FFFFFF', border: 'none', borderRadius: 100, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },

  quickSection: { padding: '12px 14px' },
  quickTitle: { fontSize: 13, fontWeight: 600, color: '#94A3B8', marginBottom: 10 },
  quickGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  quickBtn: { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 100, padding: '8px 14px', fontSize: 13, color: '#374151', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, transition: 'all 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },

  inputSection: { background: '#FFFFFF', borderTop: '1px solid #F1F5F9', padding: '12px 14px 20px' },
  inputBox: { display: 'flex', alignItems: 'flex-end', gap: 8, background: '#F8FAFF', border: '2px solid #E2E8F0', borderRadius: 16, padding: '8px 10px 8px 16px', transition: 'border-color 0.2s' },
  textarea: { flex: 1, border: 'none', background: 'transparent', fontSize: 15, resize: 'none', outline: 'none', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, maxHeight: 120, color: '#0F172A' },
  inputActions: { display: 'flex', alignItems: 'center', gap: 4 },
  iconBtn: { background: 'none', border: 'none', padding: '6px', cursor: 'pointer', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sendBtn: { width: 38, height: 38, borderRadius: 12, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 },
  btnSpinner: { width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#FFFFFF', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },
  imagePreviewBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#F8FAFF', borderRadius: '12px 12px 0 0', padding: '8px 14px', borderBottom: '1px solid #E2E8F0', margin: '0' },
  imagePreviewThumb: { width: 40, height: 40, borderRadius: 8, objectFit: 'cover', border: '1px solid #E2E8F0' },
  imagePreviewName: { flex: 1, fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  removeImageBtn: { background: '#EF4444', color: '#FFFFFF', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 },
  msgImageThumb: { width: '100%', maxWidth: 200, borderRadius: 8, marginBottom: 8, display: 'block' },
};
